import React, { useEffect, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import "./dashboard.css";
import { Link } from "react-router-dom";
import TaskWidget from "../widgets/TaskWidget";
import RevenueChart from "./Accounting/RevenueChart";
import dashInv from "../components/Images/dash-inv.png";
import dashEst from "../components/Images/dash-est.png";
import dashCust from "../components/Images/dash-cust.png";
import dashReceipt from "../components/Images/dash-receipt.png";
import LineChart from "./LineChart";
import BarChart from "./BarChart";
import { useCompany } from "../context/CompanyContext";
import { motion } from "framer-motion";
import { PlusCircle, FileText, Receipt, Layers } from "lucide-react";

const Dashboard = ({ userProfile }) => {
  const { companyId } = useCompany();

  const [setInvoices] = useState([]);
  const [setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [setReceipts] = useState([]);

  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totalEstimates, setTotalEstimates] = useState(0);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);

  // Fetch everything once we know our company_id (UNCHANGED)
  useEffect(() => {
    if (!companyId) return;

    const fetchCompanyData = async () => {
      try {
        const [
          { data: invoicesData = [], error: invErr },
          { data: estimatesData = [], error: estErr },
          { data: customersData = [], error: custErr },
          { data: receiptsData = [], error: recErr },
        ] = await Promise.all([
          supabase.from("invoices").select("*").eq("company_id", companyId),
          supabase.from("estimates").select("*").eq("company_id", companyId),
          supabase.from("customers").select("*").eq("company_id", companyId),
          supabase.from("receipts").select("*").eq("company_id", companyId),
        ]);

        if (invErr || estErr || custErr || recErr) {
          console.error(
            "Dashboard fetch error:",
            invErr || estErr || custErr || recErr
          );
          return;
        }

        setInvoices(invoicesData);
        setEstimates(estimatesData);
        setCustomers(customersData);
        setReceipts(receiptsData);

        setTotalInvoices(
          invoicesData.reduce((sum, i) => sum + (i.total || 0), 0)
        );
        setTotalEstimates(
          estimatesData.reduce((sum, e) => sum + (e.amount || 0), 0)
        );
        setTotalReceipts(
          receiptsData.reduce((sum, r) => sum + (r.total || 0), 0)
        );
      } catch (err) {
        console.error("Unexpected dashboard error:", err);
      }
    };

    const fetchMonthlyRevenue = async () => {
      try {
        const { data, error } = await supabase.rpc("get_monthly_revenue", {
          p_company_id: companyId, // assume you updated your SQL func accordingly
        });
        if (error) {
          console.error("Error fetching monthly revenue:", error);
          return;
        }
        const arr = Array(12).fill(0);
        data.forEach(({ month, total }) => {
          arr[month - 1] = total;
        });
        setMonthlyRevenue(arr);
      } catch (err) {
        console.error(err);
      }
    };

    fetchCompanyData();
    fetchMonthlyRevenue();
  }, [companyId, setEstimates, setInvoices, setReceipts]);

  // Loader state (UNCHANGED behavior, just styled)
  if (!companyId) {
    return (
      <div className="dashboard-container">
        <div className="glass panel center">
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
  };
  const stagger = { show: { transition: { staggerChildren: 0.06 } } };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="tx-header">
        <div className="tx-title">
          <h1>
            Welcome
            {userProfile?.first_name ? `, ${userProfile.first_name}` : "!"}
          </h1>
          <p className="tx-subtitle">
            Here’s what’s happening across your company.
          </p>
        </div>

        <div className="dash-actions">
          <Link to="/estimate" className="btn-ghost">
            <Layers size={16} /> New Estimate
          </Link>
          <Link to="/invoice" className="btn-ghost">
            <FileText size={16} /> New Invoice
          </Link>
          <Link to="/receipt" className="btn-primary">
            <Receipt size={16} /> Record Receipt
          </Link>
        </div>
      </header>

      {/* Pills */}
      <section className="dash-pills">
        <div className="pill">
          <span className="pill-label">Invoices</span>
          <strong>₦{totalInvoices.toLocaleString()}</strong>
        </div>
        <div className="pill">
          <span className="pill-label">Estimates</span>
          <strong>₦{totalEstimates.toLocaleString()}</strong>
        </div>
        <div className="pill">
          <span className="pill-label">Receipts</span>
          <strong>₦{totalReceipts.toLocaleString()}</strong>
        </div>
        <div className="pill">
          <span className="pill-label">Customers</span>
          <strong>{customers.length}</strong>
        </div>
      </section>

      {/* KPI Cards */}
      <motion.section
        className="kpi-grid"
        variants={stagger}
        initial="hidden"
        animate="show"
      >
        <motion.div className="kpi-card" variants={fadeUp}>
          <div className="kpi-meta">
            <h3>
              Total Invoices Issued <span className="sub">(YTD)</span>
            </h3>
            <img alt="Invoice" src={dashInv} />
          </div>
          <p className="kpi-val">₦{totalInvoices.toLocaleString()}</p>
        </motion.div>

        <motion.div className="kpi-card" variants={fadeUp}>
          <div className="kpi-meta">
            <h3>
              Total Estimates Issued <span className="sub">(YTD)</span>
            </h3>
            <img alt="Estimate" src={dashEst} />
          </div>
          <p className="kpi-val">₦{totalEstimates.toLocaleString()}</p>
        </motion.div>

        <motion.div className="kpi-card" variants={fadeUp}>
          <div className="kpi-meta">
            <h3>
              Total Customers <span className="sub">(Count)</span>
            </h3>
            <img alt="Customer" src={dashCust} />
          </div>
          <p className="kpi-val">{customers.length}</p>
        </motion.div>

        <motion.div className="kpi-card" variants={fadeUp}>
          <div className="kpi-meta">
            <h3>
              Total Receipts Issued <span className="sub">(YTD)</span>
            </h3>
            <img alt="Receipt" src={dashReceipt} />
          </div>
          <p className="kpi-val">₦{totalReceipts.toLocaleString()}</p>
        </motion.div>
      </motion.section>

      {/* Revenue Chart */}
      <section className="panel chart-lg">
        <div className="panel-head">
          <h3>Total Revenue (Month on Month)</h3>
        </div>
        <div className="panel-body">
          <RevenueChart revenueData={monthlyRevenue} />
        </div>
      </section>

      {/* Two charts */}
      <section className="two-grid">
        <div className="panel chart-sm">
          <div className="panel-head">
            <h3>Top 3 Clients by Number of Projects</h3>
          </div>
          <div className="panel-body">
            <LineChart companyId={companyId} />
          </div>
        </div>
        <div className="panel chart-sm">
          <div className="panel-head">
            <h3>Top 3 Clients by Revenue</h3>
          </div>
          <div className="panel-body">
            <BarChart companyId={companyId} />
          </div>
        </div>
      </section>

      {/* Tasks / extra */}
      <section className="panel tasks">
        <div className="panel-head">
          <h3>Tasks</h3>
          <Link to="/estimate" className="tiny-link">
            <PlusCircle size={14} /> Quick add
          </Link>
        </div>
        <div className="panel-body">
          <TaskWidget companyId={companyId} />
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
